/**
 * テナント詳細（08 §5.1/§5.3・03 §8 ライフサイクル）。
 *
 * 基本情報＋スタッフ数・接続数。状態に応じた操作（有効化／停止／再開）。
 * 停止・再開・有効化は**実行前確認＋監査ログ**（NFR-04・08 §5.3）＝停止は理由必須。
 *
 * PII は出さない（§5.1）＝ここは件数と状態まで。行の中身（受注明細・担当者名）は扱わない。
 */

import { Badge, Button, Card, ConfirmDialog, ErrorNote, formatDateTime, PageHeader, Spinner, Table, Td } from "@kenzai/ui";
import { ops, type OpsTenantUser, type ReissueSetupLinkResult } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { tenantStatusLabel, tenantStatusTone } from "./tenantStatus";

type Dialog =
  | null
  | { kind: "activate" }
  | { kind: "suspend" }
  | { kind: "resume" }
  // T-D20：パスワード設定URLの再発行（FR-112②）＝対象の利用者を持つ
  | { kind: "reissue"; user: OpsTenantUser };

export function TenantDetail() {
  const { tenantId } = useParams();
  const id = Number(tenantId);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<unknown>(null);
  // 再発行のURLは**1回だけ**返る（DB は SHA-256）＝閉じるまで画面に置く（08 §5.2 と同じ扱い）
  const [issued, setIssued] = useState<ReissueSetupLinkResult | null>(null);

  const reissue = useMutation({
    mutationFn: ({ user, reason }: { user: OpsTenantUser; reason: string }) =>
      ops.reissueSetupLink(id, user.id, reason),
    onSuccess: (result) => {
      setDialog(null);
      setError(null);
      setIssued(result);
      qc.invalidateQueries({ queryKey: ["ops-tenant-users", id] });
    },
    onError: (e) => {
      setDialog(null);
      setError(e);
    },
  });

  const q = useQuery({ queryKey: ["ops-tenant", id], queryFn: () => ops.tenant(id) });

  const act = useMutation({
    mutationFn: async ({ kind, reason }: { kind: "activate" | "suspend" | "resume"; reason: string }) => {
      if (kind === "activate") return ops.activate(id);
      if (kind === "resume") return ops.resume(id);
      return ops.suspend(id, reason);
    },
    onSuccess: () => {
      setDialog(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["ops-tenant", id] });
      qc.invalidateQueries({ queryKey: ["ops-tenants"] });
    },
    onError: (e) => {
      setDialog(null);
      setError(e);
    },
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorNote error={q.error} />;
  const d = q.data!;

  return (
    <>
      <Link to="/" style={{ color: "var(--kz-color-primary)", fontSize: "var(--kz-font-size-sm)" }}>
        ← テナント一覧へ
      </Link>

      <div style={{ margin: "var(--kz-space-3) 0 var(--kz-space-5)" }}>
        <PageHeader
          title={d.name}
          subtitle={d.code}
          actions={
            <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)" }}>
              <Badge tone={tenantStatusTone(d.status)}>{tenantStatusLabel(d.status)}</Badge>
              {d.is_demo && <Badge tone="neutral">デモ</Badge>}
            </div>
          }
        />
      </div>

      <ErrorNote error={error} />

      <Card>
        <dl
          style={{
            margin: 0,
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "var(--kz-space-3) var(--kz-space-5)",
            fontSize: "var(--kz-font-size-md)",
          }}
        >
          <Dt>状態</Dt>
          <dd style={{ margin: 0 }}>
            <Badge tone={tenantStatusTone(d.status)}>{tenantStatusLabel(d.status)}</Badge>
          </dd>
          <Dt>プラン</Dt>
          <dd style={{ margin: 0 }}>{d.plan ?? "—"}</dd>
          <Dt>デモ環境</Dt>
          <dd style={{ margin: 0 }}>{d.is_demo ? "はい" : "いいえ"}</dd>
          <Dt>スタッフ数</Dt>
          <dd style={{ margin: 0 }}>
            <span className="kz-num">{d.staff_count}</span> 名
          </dd>
          <Dt>接続数</Dt>
          <dd style={{ margin: 0 }}>
            <span className="kz-num">{d.connection_count}</span> 件
          </dd>
          <Dt>開設日</Dt>
          <dd style={{ margin: 0 }} className="kz-num">
            {formatDateTime(d.created_at)}
          </dd>
        </dl>
      </Card>

      <StaffCard tenantId={id} onReissue={(user) => setDialog({ kind: "reissue", user })} />

      {issued && <IssuedLink result={issued} onClose={() => setIssued(null)} />}

      <Card style={{ marginTop: "var(--kz-space-4)" }}>
        <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>操作</h2>
        <div style={{ display: "flex", gap: "var(--kz-space-3)", flexWrap: "wrap" }}>
          {d.status === "trial" && (
            <div style={{ width: 180 }}>
              <Button fullWidth data-testid="tenant-activate" onClick={() => setDialog({ kind: "activate" })}>
                有効化する
              </Button>
            </div>
          )}
          {(d.status === "trial" || d.status === "active") && (
            <div style={{ width: 160 }}>
              <Button fullWidth variant="ghost" data-testid="tenant-suspend" onClick={() => setDialog({ kind: "suspend" })}>
                停止する
              </Button>
            </div>
          )}
          {d.status === "suspended" && (
            <div style={{ width: 160 }}>
              <Button fullWidth data-testid="tenant-resume" onClick={() => setDialog({ kind: "resume" })}>
                再開する
              </Button>
            </div>
          )}
        </div>
        <p style={{ margin: "var(--kz-space-3) 0 0", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          操作は記録（監査ログ）に残ります。
        </p>
      </Card>

      {dialog?.kind === "activate" && (
        <ConfirmDialog
          title="このテナントを有効化しますか？"
          body="試用から稼働中に切り替えます。卸はこれまでどおりログインできます。この操作は記録に残ります。"
          confirmLabel="有効化する"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "activate", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "suspend" && (
        <ConfirmDialog
          title="このテナントを停止しますか？"
          body="停止すると卸はログインできなくなります（職人側は「現在利用できません」）。理由は記録に残ります。"
          confirmLabel="停止する"
          tone="danger"
          requireReason
          busy={act.isPending}
          onConfirm={(reason) => act.mutate({ kind: "suspend", reason })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "reissue" && (
        <ConfirmDialog
          title="パスワード設定URLを再発行しますか？"
          body={`${dialog.user.email} の設定URLを作り直します。前に発行したURLは使えなくなり、ログインの一時停止（連続失敗によるロック）も解除されます。理由は記録に残ります。`}
          confirmLabel="再発行する"
          requireReason
          busy={reissue.isPending}
          onConfirm={(reason) => reissue.mutate({ user: dialog.user, reason })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "resume" && (
        <ConfirmDialog
          title="このテナントを再開しますか？"
          body="停止中から稼働中に戻します。卸は再びログインできるようになります。この操作は記録に残ります。"
          confirmLabel="再開する"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "resume", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

function Dt({ children }: { children: React.ReactNode }) {
  return <dt style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>{children}</dt>;
}

/** スタッフ一覧（08 §5.1）＋パスワード設定URLの再発行（FR-112②・T-D20）。 */
function StaffCard({
  tenantId,
  onReissue,
}: {
  tenantId: number;
  onReissue: (user: OpsTenantUser) => void;
}) {
  const q = useQuery({
    queryKey: ["ops-tenant-users", tenantId],
    queryFn: () => ops.tenantUsers(tenantId),
  });

  return (
    <Card style={{ marginTop: "var(--kz-space-4)" }}>
      <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
        卸の利用者
      </h2>
      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorNote error={q.error} />
      ) : (
        <Table head={["メールアドレス", "氏名", "権限", "状態", "最終ログイン", "操作"]}>
          {(q.data?.items ?? []).map((u) => (
            <tr key={u.id} data-testid={`tenant-user-${u.id}`}>
              <Td>{u.email}</Td>
              <Td>{u.name ?? "—"}</Td>
              <Td>{u.role === "admin" ? "管理者" : "スタッフ"}</Td>
              <Td>
                {u.status === "invited" ? (
                  <Badge tone="warn">パスワード未設定</Badge>
                ) : u.status === "disabled" ? (
                  <Badge>無効</Badge>
                ) : u.locked_until ? (
                  <Badge tone="warn">ログイン一時停止中</Badge>
                ) : (
                  <Badge tone="primary">利用中</Badge>
                )}
              </Td>
              <Td>{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</Td>
              <Td>
                {u.status !== "disabled" && (
                  <button
                    type="button"
                    data-testid={`reissue-${u.id}`}
                    onClick={() => onReissue(u)}
                    style={{
                      minHeight: 36,
                      padding: "0 var(--kz-space-3)",
                      border: "1px solid var(--kz-color-border)",
                      borderRadius: "var(--kz-radius-sm)",
                      background: "var(--kz-color-surface)",
                      font: "inherit",
                      fontSize: "var(--kz-font-size-sm)",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    設定URLを再発行
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      )}
      <p
        style={{
          margin: "var(--kz-space-3) 0 0",
          fontSize: "var(--kz-font-size-sm)",
          color: "var(--kz-color-ink-muted)",
        }}
      >
        パスワードをお忘れの方には、設定URLを再発行してお伝えください。操作は記録に残ります。
      </p>
    </Card>
  );
}

/** 発行したURL＝**一度だけ表示**（08 §5.2 の開設結果と同じ扱い）。導入担当が本人へ伝える。 */
function IssuedLink({ result, onClose }: { result: ReissueSetupLinkResult; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  // ★**絶対URLを組み立てない**（コピーだけ壊れていた）。`:5173` は console の dev サーバのポート、
  //   `window.location.hostname` は**運営ポータル自身**のホスト＝stg では `kz-stg-ops.pages.dev`、
  //   prod では `ops.…:5173` という開けないURLになる。ops は卸コンソールの公開オリジンを
  //   知る設定を持っていないので、開設ウィザード（TenantOpen）と同じく**パスだけ**渡す。
  //   表示とコピーの中身も一致する（以前は画面にパス・コピーに壊れたURLで食い違っていた）。

  return (
    <Card
      style={{ marginTop: "var(--kz-space-4)", borderColor: "var(--kz-color-primary)" }}
      data-testid="issued-setup-link"
    >
      <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
        設定URLを再発行しました
      </h2>
      <p style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-sm)" }}>
        {result.user.email} 宛のURLです。<strong>この画面を離れると二度と表示できません。</strong>
        <strong>卸コンソールのURLに続けて</strong>開いてもらってください（有効期限{" "}
        {formatDateTime(result.expires_at)}）。
      </p>
      <code
        style={{
          display: "block",
          padding: "var(--kz-space-3)",
          background: "var(--kz-color-surface-sunken)",
          borderRadius: "var(--kz-radius-sm)",
          wordBreak: "break-all",
        }}
      >
        {result.setup_path}
      </code>
      <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-3)" }}>
        <div style={{ width: 160 }}>
          <Button fullWidth
            variant="ghost"
            data-testid="copy-setup-link"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(result.setup_path);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              } catch {
                setCopied(false);
              }
            }}
          >
            {copied ? "コピーしました" : "パスをコピー"}
          </Button>
        </div>
        <div style={{ width: 120 }}>
          <Button fullWidth variant="ghost" data-testid="close-setup-link" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </Card>
  );
}
